/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1690848000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Aug 01 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "clevertap",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Supported types",
          "depth": 0,
          "id": "supported-types"
        }, {
          "value": "Syncing objects",
          "depth": 0,
          "id": "syncing-objects"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Syncing events",
          "depth": 0,
          "id": "syncing-events"
        }, {
          "value": "Event name",
          "depth": 1,
          "id": "event-name"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching-1"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping-1"
        }, {
          "value": "Syncing segments",
          "depth": 0,
          "id": "syncing-segments"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      code: "code",
      ul: "ul",
      li: "li",
      strong: "strong",
      h3: "h3",
      a: "a"
    }, _provideComponents(), props.components), {EventSyncWarning} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to Setting > Project in CleverTap to view credentials.\n", _jsx(_components.img, {
          src: "destinations/destination-clevertap-navigate-settings.png",
          alt: ""
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["From the Project tab, copy ", _jsx(_components.code, {
          children: "Project Id"
        }), " as ", _jsx(_components.code, {
          children: "Account Id"
        }), " and ", _jsx(_components.code, {
          children: "Passcode"
        }), " into Hightouch. Use ", _jsx(_components.code, {
          children: "CleverTap Region"
        }), " for ", _jsx(_components.code, {
          children: "Localization"
        }), ".\n", _jsx(_components.img, {
          src: "destinations/destination-clevertap-credentials.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h2, {
        id: "supported-types",
        children: "Supported types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following CleverTap types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Objects"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Events"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Segments"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-objects",
        children: "Syncing objects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following CleverTap objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Profile"
          }), " Hightouch allows you to sync user profiles from a source into CleverTap."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " - In upsert mode, Hightouchs adds new rows and keeps existing rows up to date."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "For each user profile, a user identifier is required. This is the key that CleverTap uses to find the user whose profile needs to be updated. You have to set a value for one of these parameters to identify the user: Identity, CleverTap Global Object ID, Facebook ID or Google Plus ID. If this identity is not found a new user profile will be created."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-clevertap-idmappings.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You may also provide additional identifiers through Field Mapping. Profiles\nare automatically unified by CleverTap."
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to CleverTap."
      }), "\n", _jsxs(_components.p, {
        children: ["CleverTap ", _jsx(_components.strong, {
          children: "highly recommends"
        }), " you provide common fields like ", _jsx(_components.code, {
          children: "Name, Email, Phone, etc."
        }), ". You can see additional details in the ", _jsx(_components.a, {
          href: "https://developer.clevertap.com/docs/concepts-user-profiles#manually-updating-predefined-user-profile-properties",
          children: "CleverTap docs"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Phone numbers must use E164 formatting. CleverTap will reject rows that aren't in this format."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Additional fields that fits your unique use case can be mapped through custom mappings.\n", _jsx(_components.img, {
          src: "destinations/destination-clevertap-custom-fields.png",
          alt: ""
        })]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "CleverTap automatically generates schema for custom fields you provide. You\ncan see existing schemas by navigating to Setting > Schema > User\nProperties in CleverTap."
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "your source"
        }), " as new events and sends them to CleverTap when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["You can provide a fixed event name or use a value from your source as event name by toggling the switch button.\n", _jsx(_components.img, {
          src: "destinations/destination-clevertap-event-name.png",
          alt: ""
        })]
      }), "\n", _jsx(Info, {
        children: "New event names are automatically added to CleverTap schema."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "For each event, a user identifier is required. This is the key that CleverTap uses to find the user whose profile needs to be updated. You have to set a value for one of these parameters to identify the user: Identity, CleverTap Global Object ID, Facebook ID or Google Plus ID. If this identity is not found a new user profile will be created."
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync any column from your source to into any event field in CleverTap."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-segments",
        children: "Syncing segments"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing segments or custom lists, CleverTap requires the users in the segment to have already been synced to CleverTap. You can use ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-with-sequences",
          children: "sequences"
        }), " to ensure that users are synced before syncing segments."]
      }), "\n", _jsx(_components.p, {
        children: "To start syncing, choose a field to identify users (Identity, CleverTap Global Object ID, Facebook ID, or Google Plus ID), name the segment, and assign an admin's email for ownership."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "CleverTap automatically generates schema for custom fields you provide. You\ncan see generated and existing schemas by navigating to Setting > Schema\n> Events in CleverTap."
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
